import React, { useState } from "react";
// Replaced base44 with our local Core integration
import { UploadFile, InvokeLLM } from "../../integrations/Core";
// Using standard Framer Motion
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Loader2, LineChart, Sparkles } from "lucide-react";
import { Card } from "../../components/ui/card";
import AiLayout from "../../components/analyze/AiLayout"; // Kept the layout wrapper

// Imported the NEW components we just created across the file system
import ImageUploader from "../../components/forex/ImageUploader";
import AnalysisResults from "../../components/forex/AnalysisResults";
import AnalysisHistory from "../../components/forex/AnalysisHistory";
import AnalysisChat from "../../components/forex/AnalysisChat";

export default function AnalyzePage() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentAnalysis, setCurrentAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Mock history state since we don't have base44 backend
    const [analyses, setAnalyses] = useState([]);

    const handleImageSelect = (file) => {
        setSelectedFile(file);
        setCurrentAnalysis(null);
    };

    const analyzeChart = async () => {
        if (!selectedFile) return;

        setIsAnalyzing(true);

        try {
            // 1. Upload File
            const { file_url } = await UploadFile({ file: selectedFile });

            // 2. Prepare Prompt
            const prompt = `You are a highly experienced forex trader with over 20 years of expertise in technical analysis, fundamental analysis, and risk management. Your reputation depends on providing ACCURATE and ACTIONABLE trading signals.

CRITICAL INSTRUCTIONS:
- Analyze the chart image with extreme precision and attention to detail
- BE DECISIVE: Provide a clear BUY or SELL signal based on the preponderance of technical evidence
- HOLD signals should be RARE - only use when the chart is completely unclear, unreadable, or shows perfectly balanced contradictory signals
- If there's ANY discernible trend, pattern, or technical setup, provide a directional signal (BUY or SELL)
- Traders need actionable signals - use your expertise to make informed trading decisions
- All price levels MUST be realistic and based on visible support/resistance levels
- Even if confidence is Medium or Low, still provide a BUY or SELL signal with appropriate risk management

ANALYSIS CHECKLIST:
1. CHART IDENTIFICATION:
   - Identify the exact forex pair shown (e.g., EUR/USD, GBP/JPY)
   - Determine the timeframe (M5, M15, H1, H4, D1, etc.)

2. TECHNICAL ANALYSIS (Required):
   - Identify current trend (uptrend, downtrend, ranging)
   - Locate key support and resistance levels
   - Identify chart patterns (head & shoulders, double top/bottom, triangles, flags, etc.)
   - Check for candlestick patterns (doji, engulfing, hammer, shooting star, etc.)
   - Analyze visible indicators (RSI, MACD, Moving Averages, Bollinger Bands, etc.)
   - Check for divergences
   - Assess volume/momentum if visible

3. SIGNAL DECISION (BUY/SELL - Avoid HOLD):
   - BUY: Clear bullish evidence (break above resistance, bullish patterns, oversold conditions, uptrend momentum)
   - SELL: Clear bearish evidence (break below support, bearish patterns, overbought conditions, downtrend momentum)
   - HOLD: ONLY if chart is completely unreadable, no pair visible, or perfectly balanced contradictory signals (USE RARELY)
   
   DECISION MAKING PRIORITY:
   * If price is above key moving averages and trending up → BUY
   * If price is below key moving averages and trending down → SELL
   * If breaking above resistance → BUY
   * If breaking below support → SELL
   * If bullish candlestick patterns present → BUY
   * If bearish candlestick patterns present → SELL
   * If ranging but near support → BUY (bounce play)
   * If ranging but near resistance → SELL (rejection play)

4. ENTRY PRICE:
   - Must be a realistic price based on current market level shown in the chart
   - For BUY: Entry at breakout level, pullback to support, or current price if momentum is strong
   - For SELL: Entry at breakdown level, pullback to resistance, or current price if momentum is strong

5. STOP LOSS (Critical for Risk Management):
   - Place below the nearest support level for BUY signals
   - Place above the nearest resistance level for SELL signals
   - Should be 1-2% of account capital maximum
   - Must be clearly visible on the chart
   - Tighter stops for ranging markets, wider for trending markets

6. TAKE PROFIT LEVELS (Progressive Targets):
   - TP1: Conservative target at nearest resistance/support (Risk:Reward 1:1 to 1:1.5)
   - TP2: Moderate target at next key level (Risk:Reward 1:2 to 1:2.5)
   - TP3: Aggressive target at major level (Risk:Reward 1:3+)
   - All levels must be based on chart structure

7. CONFIDENCE LEVEL:
   - High: Multiple confirming indicators, clear trend, strong pattern
   - Medium: Some confirming signals, tradeable setup but watch carefully
   - Low: Weak signals but still directional bias - use smaller position size
   
   NOTE: Even Low confidence can produce BUY/SELL signals - adjust position sizing accordingly

8. DETAILED EXPLANATION:
   - State what you see in the chart specifically
   - Explain the technical evidence supporting your signal
   - Mention any risk factors or considerations
   - Be specific about price levels and patterns visible
   - Justify why you chose BUY or SELL over HOLD

RESPONSE FORMAT:
Provide specific numeric values for all price levels. Ensure consistency - if you say BUY at 1.0850, stop loss should be below (e.g., 1.0820), and take profits should be above (1.0880, 1.0910, 1.0950).

Remember: Traders need ACTION. Be decisive and provide clear directional signals. HOLD should be less than 5% of your analyses. When in doubt, identify the stronger bias and trade in that direction with appropriate risk management.`;

            // 3. Invoke LLM (Real Backend Call)
            const result = await InvokeLLM({
                prompt: prompt,
                file: selectedFile,
                response_json_schema: {
                    type: "object",
                    properties: {
                        pair: { type: "string", description: "The forex pair being analyzed (e.g. EUR/USD)" },
                        signal: { type: "string", enum: ["BUY", "SELL", "HOLD"], description: "The trading signal recommendation" },
                        explanation: { type: "string", description: "Detailed explanation of the market analysis" },
                        entry_price: { type: "string", description: "Recommended entry price" },
                        take_profit_1: { type: "string", description: "First take profit level" },
                        take_profit_2: { type: "string", description: "Second take profit level" },
                        take_profit_3: { type: "string", description: "Third take profit level" },
                        stop_loss: { type: "string", description: "Stop loss level" },
                        confidence: { type: "string", description: "Confidence level of the signal" },
                        analysis_type: { type: "string", description: "Type of analysis (Technical, Fundamental, or Combined)" },
                        timeframe: { type: "string", description: "Trading timeframe" }
                    },
                    required: ["pair", "signal", "explanation"]
                }
            });

            const analysisWithImage = {
                ...result,
                image_url: file_url,
                id: Date.now(),
                created_date: new Date().toISOString()
            };

            setCurrentAnalysis(analysisWithImage);
            // Save to local history
            setAnalyses(prev => [analysisWithImage, ...prev]);

        } catch (error) {
            console.error("Error analyzing chart:", error);
            alert(error.message || "Failed to analyze chart. Please try again.");
        }

        setIsAnalyzing(false);
    };

    const handleSelectHistoricalAnalysis = (analysis) => {
        setCurrentAnalysis(analysis);
        setSelectedFile(null); // Clear selected file when viewing history
    };

    return (
        <AiLayout>
            <div className="min-h-screen bg-black relative overflow-hidden rounded-3xl border border-gray-800">
                {/* Decorative Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
                    {/* Dollar Signs */}
                    <div className="absolute top-20 left-10 text-amber-500 text-9xl font-bold rotate-12">$</div>
                    <div className="absolute top-40 right-20 text-amber-500 text-7xl font-bold -rotate-12">$</div>
                    <div className="absolute bottom-32 left-1/4 text-amber-500 text-6xl font-bold rotate-45">$</div>
                    <div className="absolute bottom-20 right-1/3 text-amber-500 text-8xl font-bold -rotate-6">$</div>

                    {/* Candlestick Patterns */}
                    <svg className="absolute top-1/4 right-10" width="200" height="300" viewBox="0 0 200 300">
                        <line x1="40" y1="50" x2="40" y2="250" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="30" y="100" width="20" height="80" fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <line x1="80" y1="30" x2="80" y2="270" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="70" y="60" width="20" height="150" fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <line x1="120" y1="80" x2="120" y2="230" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="110" y="120" width="20" height="60" fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <line x1="160" y1="40" x2="160" y2="260" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="150" y="90" width="20" height="120" fill="none" stroke="#D4AF37" strokeWidth="2" />
                    </svg>

                    <svg className="absolute bottom-10 left-20" width="150" height="200" viewBox="0 0 150 200">
                        <line x1="30" y1="40" x2="30" y2="180" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="20" y="70" width="20" height="70" fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <line x1="70" y1="20" x2="70" y2="160" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="60" y="50" width="20" height="80" fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <line x1="110" y1="60" x2="110" y2="190" stroke="#D4AF37" strokeWidth="2" opacity="0.3" />
                        <rect x="100" y="100" width="20" height="50" fill="none" stroke="#D4AF37" strokeWidth="2" />
                    </svg>

                    {/* Chart Line Pattern */}
                    <svg className="absolute top-1/2 left-1/4 -translate-y-1/2" width="300" height="150" viewBox="0 0 300 150">
                        <path d="M 20 100 L 60 80 L 100 90 L 140 50 L 180 60 L 220 30 L 260 40"
                            stroke="#D4AF37" strokeWidth="3" fill="none" opacity="0.2" />
                    </svg>
                </div>

                {/* Grid Overlay */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.02]">
                    <div className="h-full w-full" style={{
                        backgroundImage: 'linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)',
                        backgroundSize: '50px 50px'
                    }} />
                </div>

                <div className="relative z-10 p-4 md:p-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-12"
                        >
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-2xl shadow-amber-500/30 border border-amber-400/20">
                                    <LineChart className="w-8 h-8 text-black" strokeWidth={2.5} />
                                </div>
                            </div>
                            <h1 className="text-5xl md:text-6xl font-bold text-white mb-3 tracking-tight">
                                Markets Overview
                            </h1>
                            <div className="h-1 w-32 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mb-4" />
                            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                                Upload your chart screenshot and get instant AI-powered trading signals with precise entry, take profit, and stop loss levels
                            </p>
                        </motion.div>

                        <div className="grid lg:grid-cols-3 gap-8">
                            {/* Left Column - Upload & Analysis */}
                            <div className="lg:col-span-2 space-y-6">
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <Card className="border-gray-800 bg-zinc-950/80 backdrop-blur p-6 shadow-2xl">
                                        <ImageUploader
                                            onImageSelect={handleImageSelect}
                                            isAnalyzing={isAnalyzing}
                                            selectedFile={selectedFile}
                                        />

                                        {selectedFile && !currentAnalysis && (
                                            <div className="mt-6">
                                                <Button
                                                    onClick={analyzeChart}
                                                    disabled={isAnalyzing}
                                                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold h-14 text-lg shadow-xl shadow-amber-500/25 border border-amber-400/20"
                                                >
                                                    {isAnalyzing ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                            Analyzing Chart...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-5 h-5 mr-2" />
                                                            Analyze & Generate Signal
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </Card>
                                </motion.div>

                                {/* Analysis Results */}
                                {currentAnalysis && (
                                    <>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <AnalysisResults analysis={currentAnalysis} />
                                        </motion.div>

                                        {/* Chat Component */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            <AnalysisChat analysis={currentAnalysis} />
                                        </motion.div>
                                    </>
                                )}
                            </div>

                            {/* Right Column - History */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <AnalysisHistory
                                    analyses={analyses}
                                    onSelectAnalysis={handleSelectHistoricalAnalysis}
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </AiLayout>
    );
}
